/**
 * This file contains props => style functions that encapsulate the CSS flexbox
 * layout engine.
 */
import { px } from "./units"
import { evolve, pick } from "ramda"
import { stringLiteralArray } from "./utils"

/**
 * Properties for the Parent (flex container).
 */
export type Align = keyof typeof alignments
export type AlignProp = {
  /**
   * The CSS flexbox align-items property.
   */
  align?: Align
}
export type Direction = keyof typeof directions
export type DirectionProp = {
  /**
   * The CSS flexbox flex-direction property.
   */
  direction?: Direction
}
export type Display = keyof typeof displays
export type DisplayProp = {
  /**
   * The CSS display property.
   * @default "flex"
   */
  display?: Display
}
export type Justify = keyof typeof justifications
export type JustifyProp = {
  /**
   * The CSS flexbox justify-content property.
   */
  justify?: Justify
}
export type Wrap = keyof typeof wrappings
export type WrapProp = {
  /**
   * The CSS flexbox flex-wrap property.
   */
  wrap?: Wrap
}

type FlexProperty = string | typeof flexProperties[number]
// TODO: unpack the return types from flexTransforms so the type is
// derived from the input
type FlexLonghandProperties = {
  flexGrow?: number
  flexShrink?: number
  flexBasis?: number | "auto"
}
type FlexProperties = { flex?: FlexProperty } & FlexLonghandProperties
/**
 * Properties for the Children (flex items).
 */
export type Flex = FlexProperty | FlexProperties
export type FlexProp = {
  /**
   * The CSS flexbox flex property.
   */
  flex?: Flex
}
export type Order = number
export type OrderProp = {
  /**
   * The CSS flexbox order property.
   */
  order?: Order
}
export type SelfAlignment = keyof typeof selfAlignment
export type SelfAlignProp = {
  /**
   * The CSS flexbox align-self property.
   */
  selfAlign?: SelfAlignment
}

export type Props = AlignProp &
  DirectionProp &
  DisplayProp &
  JustifyProp &
  WrapProp &
  FlexProp &
  OrderProp &
  SelfAlignProp

// TODO: use as const instead of Object.freeze
const distribution = Object.freeze({
  spaceAround: "space-around" as "space-around",
  spaceBetween: "space-between" as "space-between",
  spaceEvenly: "space-evenly" as "space-evenly"
})

const position = Object.freeze({
  center: "center" as "center",
  end: "flex-end" as "flex-end",
  start: "flex-start" as "flex-start"
})

const alignments = Object.freeze({
  ...position,
  baseline: "baseline" as "baseline",
  stretch: "stretch" as "stretch"
})
const alignmentValues = Object.values(alignments)
const align = ({
  align
}: AlignProp): {} | { align: typeof alignmentValues[number] } =>
  align === undefined
    ? {}
    : { alignItems: alignments[align as keyof typeof alignments] }

const directions = Object.freeze({
  column: "column" as "column",
  row: "row" as "row"
})

const direction = ({
  direction
}: DirectionProp): {} | { flexDirection: Direction } =>
  direction === undefined ? {} : { flexDirection: direction }

const displays = Object.freeze({
  none: "none" as "none",
  flex: "flex" as "flex",
  inlineFlex: "inline-flex" as "inline-flex"
})
const displayValues = Object.values(displays)
const display = ({
  display = "flex"
}: DisplayProp): { display: typeof displayValues[number] } => ({
  display: displays[display as keyof typeof displays]
})

const justifications = Object.freeze({
  ...position,
  ...distribution
})
const justificationValues = Object.values(justifications)
const justify = ({
  justify
}: JustifyProp): {} | { justifyContent: typeof justificationValues[number] } =>
  justify === undefined
    ? {}
    : {
        justifyContent: justifications[justify as Justify]
      }

const wrappings = Object.freeze({
  nowrap: "nowrap" as "nowrap",
  wrap: "wrap" as "wrap",
  reverse: "wrap-reverse" as "wrap-reverse"
})
const wrappingValues = Object.values(wrappings)
const wrap = ({
  wrap
}: WrapProp): {} | { flexWrap: typeof wrappingValues[number] } =>
  wrap === undefined
    ? {}
    : { flexWrap: wrappings[wrap as keyof typeof wrappings] }

const selfAlignment = Object.freeze({
  auto: "auto" as "auto",
  baseline: "baseline" as "baseline",
  stretch: "stretch" as "stretch",
  ...position
})
const selfAlignments = Object.values(selfAlignment)
const selfAlign = ({
  selfAlign
}: SelfAlignProp): {} | { alignSelf: typeof selfAlignments[number] } =>
  selfAlign === undefined
    ? {}
    : { alignSelf: selfAlignment[selfAlign as SelfAlignment] }

const order = ({ order }: OrderProp): {} | { order: Order } =>
  order === undefined ? {} : { order }

const flexProperties = stringLiteralArray(["auto", "initial", "none"])
const flexShorthandTransforms = {
  flex: (s: string) => s,
  flexGrow: (n: number) => n,
  flexShrink: (n: number) => n,
  flexBasis: (x: number | "auto") => px(x)
}
const flexShorthandProperties = Object.keys(flexShorthandTransforms)
const toFlex = evolve(flexShorthandTransforms)
const flex = ({ flex }: FlexProp) =>
  flex === undefined
    ? {}
    : typeof flex === "string"
    ? { flex }
    : toFlex(pick(flexShorthandProperties, flex))

const interpolations = {
  align,
  direction,
  display,
  flex,
  justify,
  order,
  selfAlign,
  wrap
}

export {
  alignments,
  directions,
  displays,
  justifications,
  wrappings,
  selfAlignments,
  flexProperties
}
export default interpolations
